import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Hours } from "@glowin/schemas";
import { api } from "../api.js";
import { ErrorBox } from "./MenuPage.js";
import {
  DAY_LABEL,
  EMPTY_HOURS,
  WEEKDAYS,
  addInterval,
  copyDayToAll,
  describeDay,
  PAUSE_PRESETS,
  describePause,
  isClosed,
  isPaused,
  pauseUntilIso,
  removeInterval,
  setClosed,
  updateInterval,
  validateHours
} from "../hours-model.js";
import { usePageTitle } from "../ui.js";
import {
  BanIcon,
  CheckIcon,
  CopyIcon,
  Hint,
  PlusIcon,
  TrashIcon,
  WithIcon
} from "../icons.js";

/**
 * Opening hours. Stored as local wall-clock times plus the restaurant's timezone, so
 * "we open at 11:30" stays 11:30 across daylight-saving changes.
 *
 * The phone agent uses this to know whether it can take an order at all.
 */
export function HoursPage() {
  usePageTitle("Hours");
  const { restaurantId = "" } = useParams();
  const qc = useQueryClient();
  const key = ["hours", restaurantId];

  const loaded = useQuery({ queryKey: key, queryFn: () => api.getHours(restaurantId) });
  const [hours, setHours] = useState<Hours>();

  // Seed the editor once the server value arrives (and after a save).
  useEffect(() => {
    if (loaded.data) setHours(loaded.data);
  }, [loaded.data]);

  const save = useMutation({
    mutationFn: (next: Hours) => api.putHours(restaurantId, next),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key })
  });

  // Pausing is a separate operation from saving the schedule, and intentionally so: it takes effect
  // immediately, and saving hours never resumes orders as a side effect.
  const [pauseReason, setPauseReason] = useState("");
  const pause = useMutation({
    mutationFn: (minutes: number) =>
      api.pauseOrders(restaurantId, {
        untilIso: pauseUntilIso(minutes),
        ...(pauseReason.trim() ? { reason: pauseReason.trim() } : {})
      }),
    onSuccess: () => {
      setPauseReason("");
      void qc.invalidateQueries({ queryKey: key });
    }
  });
  const resume = useMutation({
    mutationFn: () => api.resumeOrders(restaurantId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key })
  });

  if (loaded.isLoading) return <p className="muted">Loading hours…</p>;
  if (loaded.error) return <ErrorBox error={loaded.error} />;

  const value = hours ?? EMPTY_HOURS;
  const problems = validateHours(value);
  const dirty = JSON.stringify(value) !== JSON.stringify(loaded.data ?? EMPTY_HOURS);

  return (
    <div className="form-page">
      <div className="page-head">
        <h1>
          Hours <Hint>When the phone agent takes pickup orders.</Hint>
        </h1>
        <div className="page-actions">
          {dirty && <span className="muted">Unsaved changes</span>}
          <button
            className="primary"
            disabled={!dirty || problems.length > 0 || save.isPending}
            onClick={() => save.mutate(value)}
          >
            <WithIcon icon={<CheckIcon size={16} />}>{save.isPending ? "Saving…" : "Save"}</WithIcon>
          </button>
        </div>
      </div>

      {save.error ? <ErrorBox error={save.error} /> : null}
      {pause.error ? <ErrorBox error={pause.error} /> : null}
      {resume.error ? <ErrorBox error={resume.error} /> : null}

      <section className="card pause-orders">
        {isPaused(loaded.data) ? (
          <>
            <div className="notice warn">
              <strong>
                <WithIcon icon={<BanIcon size={16} />}>{describePause(loaded.data)}</WithIcon>
              </strong>
              <Hint>Callers are told you are not taking orders right now. Existing orders are unaffected.</Hint>
            </div>
            <button
              className="primary"
              disabled={resume.isPending}
              onClick={() => resume.mutate()}
            >
              <WithIcon icon={<CheckIcon size={16} />}>
                {resume.isPending ? "Resuming…" : "Resume orders"}
              </WithIcon>
            </button>
          </>
        ) : (
          <>
            <h2>
              <WithIcon icon={<BanIcon size={18} />}>Pause orders</WithIcon>
              <Hint>
                Stops new orders straight away without changing your hours, for when you are too busy or
                something is off the menu.
              </Hint>
            </h2>
            <label className="field">
              <span>
                Reason <span className="muted">(optional)</span>
                <Hint>Read to callers if they ask.</Hint>
              </span>
              <input
                value={pauseReason}
                maxLength={200}
                placeholder="Kitchen equipment down"
                onChange={(e) => setPauseReason(e.target.value)}
              />
            </label>
            <div className="row">
              {PAUSE_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  disabled={pause.isPending}
                  onClick={() => pause.mutate(preset.minutes)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </>
        )}
      </section>
      {problems.length > 0 && (
        <div className="error">
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      {save.isSuccess && !dirty ? (
        <div className="notice">
          <WithIcon icon={<CheckIcon size={16} />}>Saved</WithIcon>
        </div>
      ) : null}

      <ul className="days">
        {WEEKDAYS.map((day) => {
          const closed = isClosed(value, day);
          return (
            <li key={day} className="day">
              <div className="day-head">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={!closed}
                    onChange={(e) => setHours(setClosed(value, day, !e.target.checked))}
                  />
                  <strong>{DAY_LABEL[day]}</strong>
                </label>
                <span className="muted day-summary">{describeDay(value, day)}</span>
                <div className="day-actions">
                  {!closed && (
                    <button className="ghost" onClick={() => setHours(copyDayToAll(value, day))}>
                      <WithIcon icon={<CopyIcon size={15} />}>Copy to all</WithIcon>
                    </button>
                  )}
                  <button className="ghost" onClick={() => setHours(addInterval(value, day))}>
                    <WithIcon icon={<PlusIcon size={15} />}>Shift</WithIcon>
                  </button>
                </div>
              </div>

              {!closed && (
                <div className="shifts">
                  {(value.weekly[day] ?? []).map((iv, i) => (
                    <div key={i} className="shift">
                      <input
                        type="time"
                        aria-label={`${DAY_LABEL[day]} shift ${i + 1} opens`}
                        value={iv.open}
                        onChange={(e) =>
                          setHours(updateInterval(value, day, i, { open: e.target.value }))
                        }
                      />
                      <span className="muted">to</span>
                      <input
                        type="time"
                        aria-label={`${DAY_LABEL[day]} shift ${i + 1} closes`}
                        value={iv.close}
                        onChange={(e) =>
                          setHours(updateInterval(value, day, i, { close: e.target.value }))
                        }
                      />
                      <button
                        className="ghost icon-button"
                        onClick={() => setHours(removeInterval(value, day, i))}
                        aria-label="Remove shift"
                        title="Remove shift"
                      >
                        <TrashIcon size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <p className="muted footnote">
        Past midnight? <Hint>A closing time earlier than the opening time runs past midnight, e.g. 5:00 PM to 1:00 AM.</Hint>
      </p>
    </div>
  );
}
